/*eslint-disable*/
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle
} from "react";
import Checkbox from '@mui/material/Checkbox';
import {
  Grid,
  Typography,
  TextField,
  MenuItem,
  Divider,
  FormControl,
  Paper,
  Box,
  Select
} from "@mui/material";
import CustomDropDownPicker from "../../../components/CustomDropDownPicker";
import { STYLES } from "config";

const styles = {
  CheckboxWrapper: {
    //display: "flex",
    alignItems: "center",
    width: "100%",
    flexDirection: "row",
    gap: "8px",
    padding: " 6px",
    border: "2px solid #ccc",
    borderRadius: "4px",
    background: "#fafafa",
  },
}

// Forward ref to expose validation method
const ResidentialPropertyFields = forwardRef((props, ref) => {
  const { data, setvalue } = props;
  const [state, setstate] = useState(data);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    setstate(data);
  }, [data]);

  const validateField = (name, value) => {
    let error = "";

    if (name === "gla" && !value) {
      error = "GLA is required";
    } else if (
      (name === "price" || name === "salePrice") &&
      (!value || isNaN(value))
    ) {
      error = "Valid price is required";
    } else if (name === "price" && value && parseFloat(value) <= 50000) {
      error = "Price must be more than 50,000";
    } else if (name === "grossmonthlyRental" && (!value || isNaN(value))) {
      error = "Valid rental amount is required";
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
    return !error;
  };

  const listingTypeOptions = [
    { label: "To Let", value: "tolet" },
    { label: "For Sale", value: "sale" },
    { label: "Both", value: "both" }
  ];
  const salespricePresentation = [
    { label: "Standard", value: "Standard" },
    { label: "Poa", value: "POA" },
    { label: "Negotiable", value: "Negotiable" },
    { label: "OffersFrom", value: "OffersFrom" }
  ];

  const updateData = (e) => {
    const { name, value } = e.target;
    validateField(name, value); // Validate on change

    const updatedState = { ...state, [name]: value };
    setstate(updatedState);
    setvalue(updatedState);
  };

  const handleDropdownChange = (name, value) => {
    const updatedState = { ...state, [name]: value };
    setstate(updatedState);
    setvalue(updatedState);
  };

  const inputStyle = {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#003366"
      },
      "&:hover fieldset": {
        borderColor: "#003366"
      }
    }
  };

  // 🔥 Expose method to parent via ref
  useImperativeHandle(ref, () => ({
    isValidated() {
      // Run validation on required fields
      const requiredFields = [
        { name: "gla", value: state.gla },
      ];

      if (state.type === "sale" || state.type === "both") {
        requiredFields.push({ name: "price", value: state.price });
      }
      if (state.type === "tolet" || state.type === "both") {
        requiredFields.push({
          name: "grossmonthlyRental",
          value: state.grossmonthlyRental
        });
      }
      if (state.type === "sale" || state.type === "both") {
        requiredFields.push({
          name: "salesPricePresentation",
          value: state.salesPricePresentation
        });
      }

      let isValid = true;
      const newErrors = {};

      requiredFields.forEach(({ name, value }) => {
        if (
          !value ||
          (typeof value === "string" ? value.trim() === "" : false)
        ) {
          newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)
            } is required`;
          isValid = false;
        }
      });

      // Additional validation for price > 50000
      if ((state.type === "sale" || state.type === "both") && state.price) {
        if (parseFloat(state.price) <= 50000) {
          newErrors.price = "Price must be more than 50,000";
          isValid = false;
        }
      }

      setErrors(newErrors);
      return isValid;
    }
  }));

  const saveData = (location, data) => {
    setstate({
      ...state,
      [location]: data,
    });
  };

  return (
    <Paper elevation={5} sx={{ p: 3 }}>
      <Box sx={{ p: 2 }}>
        <Typography
          variant="h5"
          gutterBottom
          sx={{ color: STYLES.WEB_APP_FONT_CLR }}
        >
          Property Details
        </Typography>
        <Divider sx={{ bgcolor: STYLES.WEB_APP_FONT_CLR, mb: 2 }} />

        <Typography
          variant="body1"
          paragraph
          sx={{ color: STYLES.WEB_APP_FONT_CLR }}
        >
          These are the topline details of the property.
        </Typography>

        <Divider sx={{ bgcolor: STYLES.WEB_APP_FONT_CLR, mb: 2 }} />

        <Grid container spacing={2}>
          {(state?.type === "sale" || state?.type === "both") && (
            <>
              {/* Sales Price Presentation P24*/}
              <Grid item size={2}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mt: 1.5,
                    color: STYLES.SECONDARY_APP_CLR
                  }}
                >
                  Sales Price Presentation (Property 24){" "}
                </Typography>
              </Grid>
              <Grid item size={10}>
                <FormControl fullWidth sx={inputStyle}>
                  <div style={styles.CheckboxWrapper}>
                    <Checkbox
                      type="checkbox"
                      checked={state.poa || false}
                      name="poa"
                      onChange={(e) => saveData("poa", e.target.checked)}
                    />
                    <span>POA</span>
                  </div>
                </FormControl>
              </Grid>

              {/* Sales Price Presentation */}
              <Grid item size={2}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mt: 1.5,
                    color: STYLES.SECONDARY_APP_CLR
                  }}
                >
                  Sales Price Presentation (Private Property){" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
              </Grid>
              <Grid item size={10}>
                <FormControl fullWidth sx={inputStyle}>
                  <CustomDropDownPicker
                    options={salespricePresentation}
                    value={state?.salesPricePresentation}
                    error={!!errors.salesPricePresentation}
                    helperText={errors.salesPricePresentation}
                    onChange={(value) =>
                      handleDropdownChange("salesPricePresentation", value)
                    }
                    placeholder="Select Sales Price Presentation"
                    style={{
                      borderColor: STYLES.SECONDARY_APP_CLR,
                      color: STYLES.SECONDARY_APP_CLR
                    }}
                  />
                </FormControl>
              </Grid>
            </>
          )}
          {/* Plot Size */}
          <Grid item size={2}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, mt: 1.5, color: STYLES.SECONDARY_APP_CLR }}
            >
              Total Size of Property (Gross Lettable Area) m²{" "}
              <span style={{ color: "red" }}>*</span>
            </Typography>
          </Grid>
          <Grid item size={10}>
            <TextField
              fullWidth
              size="small"
              name="gla"
              value={state?.gla || ""}
              onChange={updateData}
              error={!!errors.gla}
              helperText={errors.gla}
              sx={inputStyle}
            />
          </Grid>

          {/* Bathrooms */}
          <Grid item size={2}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, mt: 1.5, color: STYLES.SECONDARY_APP_CLR }}
            >
              Bathrooms
            </Typography>
          </Grid>
          <Grid item size={10}>
            <TextField
              fullWidth
              size="small"
              name="bathrooms"
              value={state?.bathrooms || ""}
              onChange={updateData}
              error={!!errors.bathrooms}
              helperText={errors.bathrooms}
              sx={inputStyle}
            />
          </Grid>

          {/* Value of Area */}
          <Grid item size={2}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, mt: 1.5, color: STYLES.SECONDARY_APP_CLR }}
            >
              Size Measurement
            </Typography>
          </Grid>
          <Grid item size={10}>
            <FormControl fullWidth sx={inputStyle}>
              <Select
                size="small"
                name="valueOfArea"
                value={state?.valueOfArea || "m2"}
                onChange={updateData}
              >
                <MenuItem value="m2">m2</MenuItem>
                {/* <MenuItem value="sqft">Sqft</MenuItem> */}
              </Select>
            </FormControl>
          </Grid>

          {/* Currency */}
          <Grid item size={2}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, mt: 1.5, color: STYLES.SECONDARY_APP_CLR }}
            >
              Currency
            </Typography>
          </Grid>
          <Grid item size={10}>
            <FormControl fullWidth sx={inputStyle}>
              <Select
                size="small"
                name="currency"
                value={state?.currency || "ZAR"}
                onChange={updateData}
              >
                <MenuItem value="ZAR">R (ZAR)</MenuItem>
                {/* <MenuItem value="$">$ (USD)</MenuItem> */}
              </Select>
            </FormControl>
          </Grid>

          {/* Rent or Sale */}
          <Grid item size={2}>
            <Typography
              variant="body1"
              sx={{ fontWeight: 400, mt: 1.5, color: STYLES.SECONDARY_APP_CLR }}
            >
              Rent or Sale<span style={{ color: "red" }}>*</span>
            </Typography>
          </Grid>
          <Grid item size={10}>
            <FormControl fullWidth sx={inputStyle}>
              <CustomDropDownPicker
                required
                options={listingTypeOptions}
                value={state?.type || ""}
                onChange={(value) => handleDropdownChange("type", value)}
                placeholder="Select Listing Type"
                style={{
                  borderColor: STYLES.SECONDARY_APP_CLR,
                  color: STYLES.SECONDARY_APP_CLR
                }}
              />
            </FormControl>
          </Grid>

          {/* Conditional Fields */}
          {(state?.type === "sale" || state?.type === "both") && (
            <>
              <Grid item size={2}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mt: 1.5,
                    color: STYLES.SECONDARY_APP_CLR
                  }}
                >
                  Price
                </Typography>
              </Grid>
              <Grid item size={10}>
                <TextField
                  fullWidth
                  size="small"
                  name="price"
                  value={state?.price || ""}
                  onChange={updateData}
                  error={!!errors.price}
                  helperText={errors.price}
                  sx={inputStyle}
                />
              </Grid>
            </>
          )}

          {(state?.type === "tolet" || state?.type.includes("To Let")) && (
            <>
              <Grid item size={2}>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mt: 1.5,
                    color: STYLES.SECONDARY_APP_CLR
                  }}
                >
                  Gross Monthly Rental
                </Typography>
              </Grid>
              <Grid item size={10}>
                <TextField
                  fullWidth
                  size="small"
                  name="grossmonthlyRental"
                  value={state?.grossmonthlyRental || ""}
                  onChange={updateData}
                  error={!!errors.grossmonthlyRental}
                  helperText={errors.grossmonthlyRental}
                  sx={inputStyle}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Paper>
  );
});

export default ResidentialPropertyFields;
